import { Component, instantiate, Node, Prefab, ScrollView, _decorator } from 'cc';
import Lobby from '../../../System/Lobby';
import Page from '../../../../common/scripts/Toolkit/Components/Page/Page';
import ValueSelector from '../../../../common/scripts/Toolkit/Helper/ValueSelector';
import NumberFormatter from '../../../../common/scripts/Toolkit/Components/Label/NumberFormatter';
import MultiThemeSource from '../../../../common/scripts/Toolkit/Components/MultiTheme/MultiThemeSource';
import OptionValuePicker from '../../../../common/scripts/Toolkit/Components/ValuePicker/OptionValuePicker';
import ValueToggleSelector from '../../../../common/scripts/Toolkit/Components/ValuePicker/ValueToggleSelector';
import LocalizedLabel from '../../../../common/scripts/Toolkit/Components/LocalizedComponent/LocalizedLabel';
import { FeatureActivityEventName } from '../../Data/Config/FeatureActivityConfig';
import SlotGameText from '../../Data/Config/SlotGameTextSet';

const { ccclass, property, requireComponent } = _decorator;
const HOW_TO_PLAY_INDEX = 0;
const GAME_INTERFACE_INDEX = 1;
const GAME_RULE_INDEX = 2;

@ccclass('RuleContent')
class RuleContent {
    @property([Prefab])
    public readonly howToPlayPrefabs: Prefab[] = [];

    @property([Prefab])
    public readonly interfacePrefabs: Prefab[] = [];

    @property([Prefab])
    public readonly gameRulePrefabs: Prefab[] = [];

    public getPages(index: number): Prefab[] {
        switch (index) {
            case HOW_TO_PLAY_INDEX:
                return this.howToPlayPrefabs;
            case GAME_INTERFACE_INDEX:
                return this.interfacePrefabs;
            case GAME_RULE_INDEX:
                return this.gameRulePrefabs;
            default:
                throw new Error('Invalid index');
        }
    }
}

@ccclass('GameRulePage')
@requireComponent([Page, MultiThemeSource])
export default class GameRulePage extends Component {
    @property(ValueToggleSelector)
    private readonly contentToggleSelector: ValueToggleSelector = null;

    @property(OptionValuePicker)
    private readonly pageIndexPicker: OptionValuePicker = null;

    @property(LocalizedLabel)
    private readonly title: LocalizedLabel = null;

    @property(NumberFormatter)
    private readonly pageCountNumber: NumberFormatter = null;

    @property(ScrollView)
    private readonly contentScrollView: ScrollView = null;

    @property(Prefab)
    private readonly gameRulePrefab: Prefab = null;

    @property(RuleContent)
    private readonly crowdPlayContent: RuleContent = new RuleContent();

    @property(RuleContent)
    private readonly multiplePlayContent: RuleContent = new RuleContent();

    @property(RuleContent)
    private readonly roundPlayContent: RuleContent = new RuleContent();

    @property(RuleContent)
    private readonly singlePlayContent: RuleContent = new RuleContent();

    @property(RuleContent)
    private readonly roadmapContent: RuleContent = new RuleContent();

    private _page: Page = null;
    private _layoutThemeSource: MultiThemeSource = null;

    private _contentSelector: ValueSelector<number> = null;
    private _pageSelector: ValueSelector<number> = null;
    private _displayingContent: RuleContent = null;
    private _displayContent: Node = null;
    private _pageNodeMap: Map<Prefab, Node> = new Map();

    private get page() {
        if (!this._page) {
            this._page = this.getComponent(Page);
        }
        return this._page;
    }

    private get layoutThemeSource() {
        if (!this._layoutThemeSource) {
            this._layoutThemeSource = this.getComponent(MultiThemeSource);
        }
        return this._layoutThemeSource;
    }

    public onLoad() {
        this._contentSelector = new ValueSelector([HOW_TO_PLAY_INDEX, GAME_INTERFACE_INDEX, GAME_RULE_INDEX]);
        this._contentSelector.on(ValueSelector.EVENT_TYPE.VALUE_CHANGED, this._updateContent, this);
        this.contentToggleSelector.init(this._contentSelector, () => {});
        this.crowdPlayContent.gameRulePrefabs.push(this.gameRulePrefab);
        this.multiplePlayContent.gameRulePrefabs.push(this.gameRulePrefab);
        this.roundPlayContent.gameRulePrefabs.push(this.gameRulePrefab);
        this.singlePlayContent.gameRulePrefabs.push(this.gameRulePrefab);
    }

    public display(type: 'Roadmap' | 'GameRule') {
        this.contentToggleSelector.node.active = type === 'GameRule';
        this.title.key = type === 'GameRule' ? SlotGameText.GAME_RULES : SlotGameText.ROADMAP;
        this._displayingContent = this._getRuleContent(type);
        this._contentSelector.value = 0;
        // ensure the content is updated
        this._updateContent();
        this.page.open();
        Lobby.activity.send(FeatureActivityEventName.OpenGameRulePage, {});
    }

    private _updateContent() {
        const pages = this._displayingContent.getPages(this._contentSelector.currentIdx);
        const showPageSelector = pages.length > 1;
        this.pageIndexPicker.node.active = showPageSelector;
        if (this._pageSelector) {
            this._pageSelector.off(ValueSelector.EVENT_TYPE.VALUE_CHANGED, this._updatePage, this);
            this.pageIndexPicker.deinit();
        }
        this._pageSelector = new ValueSelector(pages.map((_, idx) => idx));
        this._pageSelector.on(ValueSelector.EVENT_TYPE.VALUE_CHANGED, this._updatePage, this);
        this.pageIndexPicker.init(this._pageSelector);
        this.pageCountNumber.textTemplate = `{0}/${pages.length}`;
        this.pageCountNumber.updateTextDisplay();
        this._updatePage();
        this._updateLayout();
    }

    private _updatePage() {
        if (this._displayContent) {
            this.contentScrollView.content.removeChild(this._displayContent);
        }
        const page = this._displayingContent.getPages(this._contentSelector.currentIdx)[this._pageSelector.value];
        this._displayContent =
            this._pageNodeMap.get(page) ??
            (() => {
                const node = instantiate(page);
                this._pageNodeMap.set(page, node);
                return node;
            })();
        this.contentScrollView.content.addChild(this._displayContent);
        this.contentScrollView.stopAutoScroll();
        this.contentScrollView.scrollToTop();
        this.pageCountNumber.value = this._pageSelector.value + 1;
    }

    private _updateLayout() {
        if (this.pageIndexPicker.node.active && this.contentToggleSelector.node.active) {
            this.layoutThemeSource.theme = 0;
        } else if (this.contentToggleSelector.node.active) {
            this.layoutThemeSource.theme = 1;
        } else if (this.pageIndexPicker.node.active) {
            this.layoutThemeSource.theme = 2;
        } else {
            this.layoutThemeSource.theme = 3;
        }
    }

    private _getRuleContent(type: 'Roadmap' | 'GameRule') {
        if (type === 'Roadmap') {
            return this.roadmapContent;
        }
        return this.singlePlayContent;
    }
}
